import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
    AbstractControl, FormArray,
    FormGroup,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import {PartnerCRUDService} from '../../service/partnerCRUD.service';
import {Subscription} from 'rxjs';
import {PartnerPersonType} from '../../../../modules/person/api/entity/partnerPerson.model';
import {
    PersonUpdateFormComponent
} from '../../../../../person/api/component/person-update-form/person-update-form.component';
import {PhoneInputComponent} from '../../../../../common/phone/component/phone-input/phone-input.component';
import {PartnerSavePayload} from '../../service/payload/partnerSave.payload';
import {PartnerSavePartnerPersonPayload} from '../../service/payload/partnerSavePartnerPerson.payload';
import {PartnerSaveAdditionalPropertyPayload} from '../../service/payload/partnerSaveAdditionalProperty.payload';
import {
    PARTNER_ADDITIONAL_PROPERTY_PRACTITIONER_TYPE_VALUE,
    PRACTITIONER_PARTNER_TYPE
} from '../../../../../../shared/model/practitioner/practitioner.model';
import {PersonModel} from '../../../../../person/api/entity/person.model';
import {PARTNER_SAVE_PERSON_ALREADY_PART_OF_THIS_TYPE} from '../../../../partner.module';
import {
    MarketingChannelSummaryModel,
    MarketingChannelTypes
} from '../../../../../../shared/model/marketing.channel/marketingChannelSummary.model';
import {FormUtils} from '../../../../../../shared/utils/form.utils';
import {BaseView} from '../../../../../core/view/base.view';
import {LovValueSummary} from '../../../../../common/lov/lov-value/api/model/lovValueSummary.model';
import {OfficeSummaryModel} from '../../../../../office/api/model/officeSummary.model';
import {PartnerModel, PartnerParentModel} from '../../model/partner.model';
import {PartnerAddressModel, PartnerAddressType} from '../../model/partnerAddress.model';
import {PartnerSummaryModel, PartnerType} from '../../model/partnerSummary.model';
import {IDialogComponent} from '../../../../../common/dialog/iDialog.component';
import {DialogWithInput} from '../../../../../common/dialog/dialogWithInput';
import {PartnerFindOneByIdService} from '../../service/partnerFindOneById.service';
import {PartnerSummaryFindOneByIdService} from "../../service/partnerSummaryFindOneById.service";
import {ApplicationCache} from "../../../../../common/cache/api/application.cache";

export interface PartnerSaveDialogInput {
    id?: number;
    type: PartnerType;
    additionalPartnerTypes?: string[];
    parentPartner?: PartnerParentSaveDialogInput;
    office?: OfficeSummaryModel;
}
export interface PartnerParentSaveDialogInput {
    typeValue: string;
    partner: PartnerSummaryModel;
}

export interface PartnerSaveDialogOutput {
    entity: PartnerModel;
}

@Component({
    templateUrl: './partner-save-dialog.component.html',
    providers: [
        PartnerCRUDService,
        PartnerFindOneByIdService,
        PartnerSummaryFindOneByIdService,
    ]
})
export class PartnerSaveDialogComponent extends BaseView implements
    IDialogComponent<PartnerSaveDialogInput, PartnerSaveDialogOutput>,
    OnInit, OnDestroy {

    readonly CHANNEL_PATIENT_TYPE_VALUE = 'MARKETING_CHANNEL_PATIENT';
    readonly CHANNEL_PRACTITIONER_TYPE_VALUE = 'MARKETING_CHANNEL_PRACTITIONER';
    dialog: DialogWithInput<PartnerSaveDialogInput, PartnerSaveDialogOutput>;

    partner: PartnerModel;
    PartnerType = PartnerType;
    formGroup: UntypedFormGroup;
    marketingChannelsFormArray: UntypedFormArray;
    defaultGroup: UntypedFormGroup;
    personFormGroup: UntypedFormGroup;

    addressesFormArray: UntypedFormArray;
    showPersonForm = true;
    practitionerTypes: LovValueSummary [] = [];
    displayOffice = false;
    permissions: string[] = [];
    offices: OfficeSummaryModel[] = [];
    maxNbAddresses = 1;
    allowedAddressTypes: PartnerAddressType[] = [];
    country: string;
    @ViewChild('personUpdateForm') personUpdateForm: PersonUpdateFormComponent;

    @ViewChild('phoneComponent') phoneComponent: PhoneInputComponent;
    @ViewChild('mobilePhoneComponent') mobilePhoneComponent: PhoneInputComponent;
    @ViewChild('officePhoneComponent') officePhoneComponent: PhoneInputComponent;

    subscriptions: Subscription[] = [];

    constructor(private formBuilder: UntypedFormBuilder,
                public service: PartnerCRUDService,
                public findOneService: PartnerFindOneByIdService,
                public partnerSummaryFindOneByIdService: PartnerSummaryFindOneByIdService,
                public applicationCache: ApplicationCache) {
        super();
        this.defaultGroup = this.formBuilder.group({
            name: new UntypedFormControl('', Validators.required),
            phone: new UntypedFormGroup({}),
            officePhone: new UntypedFormGroup({}),
            mobilePhone: new UntypedFormGroup({}),
            businessNumber: new UntypedFormControl(''),
            email: new UntypedFormControl(undefined),
            website: new UntypedFormControl(''),
            taxable: new UntypedFormControl(false),
            vatRegimeValue: new UntypedFormControl('PARTNER_VAT_REGIME_INCONNU'),
            personId: new UntypedFormControl(),
        });

        this.personFormGroup = this.formBuilder.group({
            person: UntypedFormGroup,
            id: new UntypedFormControl(''),
        });

        this.addressesFormArray = this.formBuilder.array([]);

        this.marketingChannelsFormArray = this.formBuilder.array([]);
        this.formGroup = this.formBuilder.group({
            partnerTypeValue: new UntypedFormControl(undefined, Validators.required),
            officeId: new UntypedFormControl(undefined, Validators.required),
            summary: this.formBuilder.group({
                id: new UntypedFormControl()
            }),
            defaultGroup: this.defaultGroup,
            note: new UntypedFormControl(undefined),
            addresses: this.addressesFormArray
        });

        this.subscriptions.push(this.applicationCache.observe<OfficeSummaryModel>('office')
            .subscribe(value => {
                if (value) {
                    this.formGroup.patchValue({
                        officeId: value.id
                    })
                }
            }));
    }

    ngOnInit() {
        this.formGroup.removeControl('personGroup');
        this.formGroup.removeControl('address');
        this.formGroup.removeControl('practitionerTypeValue');
        this.formGroup.removeControl('defaultGroup');
        this.formGroup.removeControl('marketingChannels');
        this.formGroup.patchValue({partnerTypeValue: this.dialog.input.type});
        if (this.dialog.input.type === PartnerType.PARTNER_TYPE_PATIENT) {
            this.maxNbAddresses = 1;
            this.formGroup.addControl('personGroup', this.personFormGroup);
            this.allowedAddressTypes = [PartnerAddressType.INVOICE];
            this.formGroup.addControl('marketingChannels', this.marketingChannelsFormArray);
            this.formGroup.addControl('parents', this.formBuilder.array([
                this.createParentFormGroup('PARTNER_PARENT_TYPE_CARE_HOME', 'Ehpad', PartnerType.PARTNER_TYPE_CARE_HOME),
                this.createParentFormGroup('PARTNER_PARENT_TYPE_TUTOR', 'Tuteur financier', PartnerType.PARTNER_TYPE_TUTOR),
                this.createParentFormGroup('PARTNER_PARENT_TYPE_HEALTH_INSURANCE', 'Mutuelle', PartnerType.PARTNER_TYPE_HEALTH_INSURANCE),
            ]))

            this.formGroup.addControl('parentPartnerId', this.formBuilder.control(undefined))
        } else if (this.dialog.input.type === PartnerType.PARTNER_TYPE_PRACTITIONER) {
            this.maxNbAddresses = 1;
            this.formGroup.addControl('personGroup', this.personFormGroup);
            this.allowedAddressTypes = [PartnerAddressType.MAIN];
            this.formGroup.addControl('practitionerTypeValue', new UntypedFormControl());
        } else {

            this.formGroup.addControl('defaultGroup', this.defaultGroup);
            this.allowedAddressTypes = [PartnerAddressType.INVOICE, PartnerAddressType.DELIVERY];
            this.maxNbAddresses = -1;
            this.formGroup.addControl('marketingChannels', this.marketingChannelsFormArray);

            if (this.dialog.input.type === PartnerType.PARTNER_TYPE_HEALTH_INSURANCE) {
                this.defaultGroup.removeControl('vatRegimeValue');
                this.defaultGroup.removeControl('businessNumber');
            }

        }
        if (this.dialog.input.parentPartner) {
            this.patchParent(this.dialog.input.parentPartner.typeValue,
                this.dialog.input.parentPartner.partner.id,
                this.dialog.input.parentPartner.partner);
        }
        if (this.dialog.input.office) {
            this.formGroup.patchValue({
                officeId: this.dialog.input.office.id
            });
        }

        this.setDefaultPractitionerType();

        this.showPersonForm = !this.dialog.input.id;
        this.formGroup.controls.partnerTypeValue.disable();

        // resolve permission
        if (this.dialog.input.type === PartnerType.PARTNER_TYPE_PATIENT) {
            this.permissions = this.dialog.input.id ? [
                'ACCESS_RIGHT_APP_PODOPRO_PATIENT_EDIT',
                'ACCESS_RIGHT_APP_INSOLE_SUBCONTRACT_PATIENT_EDIT',
                'ACCESS_RIGHT_APP_INSOLE_SELF_PRODUCTION_PATIENT_EDIT',
                'ACCESS_RIGHT_APP_MEDICAL_STERILIZATION_PATIENT_EDIT'
            ] : [
                'ACCESS_RIGHT_APP_PODOPRO_PATIENT_ADD',
                'ACCESS_RIGHT_APP_MEDICAL_AGENDA_PATIENT_ADD',
                'ACCESS_RIGHT_APP_INSOLE_SUBCONTRACT_PATIENT_ADD',
                'ACCESS_RIGHT_APP_INSOLE_SELF_PRODUCTION_PATIENT_ADD',
                'ACCESS_RIGHT_APP_MEDICAL_STERILIZATION_PATIENT_ADD'];
        } else if (this.dialog.input.type === PartnerType.PARTNER_TYPE_PRACTITIONER) {
            this.permissions = this.dialog.input.id ? ['ACCESS_RIGHT_APP_PODOPRO_PRACTITIONER_EDIT'] : ['ACCESS_RIGHT_APP_PODOPRO_PRACTITIONER_ADD', 'ACCESS_RIGHT_APP_INSOLE_SUBCONTRACT_PATIENT_EDIT'];
        }

        if (!this.dialog.input.id) {
            this.addDefaultAddress();
        }
    }


    ngOnDestroy(): void {
        this.subscriptions.forEach(value => value.unsubscribe());
    }

    createParentFormGroup(typeValue: string, label: string, partnerTypeValue: string): FormGroup {
        return this.formBuilder.group({
            partnerId: this.formBuilder.control(undefined),
            partner: this.formBuilder.control(undefined),
            typeValue: this.formBuilder.control(typeValue, Validators.required),
            label: this.formBuilder.control(label, Validators.required),
            partnerTypeValue: this.formBuilder.control(partnerTypeValue, Validators.required)
        })
    }

    nameUpdated(name: string) {
        if (! this.dialog.input.id && name?.length >= 3) {
            this.addressesFormArray.controls.forEach(control => {
                const recipientNameControl = control.get('address.recipientName');
                if (recipientNameControl) {
                    recipientNameControl.setValue(name);
                }
            })
        }
    }

    entitySaved(entity: PartnerModel) {
        if (entity?.summary?.office) {
            window.localStorage.setItem('currentOfficeId', ''+entity.summary.office.id);
        }

        this.dialog.close({
            entity
        });
    }

    officesRefreshed(offices: OfficeSummaryModel[]) {
        this.offices = offices;
        this.setDefaultOffice();
    }

    setDefaultOffice() {
        if (!this.officeControl.value && this.offices?.length > 0) {
            let officeSet = false;
            const currentOfficeIdStr = window.localStorage.getItem('currentOfficeId');
            if (currentOfficeIdStr) {
                const currentOfficeId = +currentOfficeIdStr;
                if (currentOfficeId && this.offices.findIndex(office => office.id === currentOfficeId) > -1) {
                    this.officeControl.setValue(currentOfficeId);
                    officeSet = true;
                }
            }
            if (!officeSet) {
                this.officeControl.setValue(this.offices[0].id);
            }
        }
        this.displayOffice = this.officeControl.value && this.offices?.length > 1;
    }


    entitySaveError(message: string) {
        if (message === PARTNER_SAVE_PERSON_ALREADY_PART_OF_THIS_TYPE) {
            this.personUpdateForm.entityLoaded(undefined);
            this.showPersonForm = false;
        }
    }

    entityLoaded(entity: PartnerModel) {
        const type = entity.summary.types.length === 1 ? entity.summary.types[0] : entity.summary.types.find(value => value.main === true);
        this.partner = entity;

        this.formGroup.patchValue({
            partnerTypeValue: type.type.value,
            summary: entity.summary,
            officeId: entity.summary.office?.id,
            note: entity.summary.note,
            parentPartnerId: entity.summary.parentPartnerId
        });

        this.setDefaultOffice();

        FormUtils.clearFormArray(this.marketingChannelsFormArray);
        entity.marketingChannels.map(value => this.mapMarketingChannnel(value)).forEach(formGroup => {
            this.marketingChannelsFormArray.push(formGroup);
        });

        entity.addresses.forEach(address => {
            this.addressesFormArray.push(this.newAddress(address));
        });

        if (entity.summary.hasType(PartnerType.PARTNER_TYPE_TUTOR) || entity.summary.hasType(PartnerType.PARTNER_TYPE_HEALTH_INSURANCE)) {
            this.defaultGroup.removeControl('vatRegimeValue');
            this.defaultGroup.removeControl('businessNumber');
        }

        entity.parents.forEach(value => {
            this.patchParent(value.type.value, value.partnerId)
        });


        console.log(['entityLoaded', entity, this.isPatient, this.isPractitioner]);
        if (this.isPatient || this.isPractitioner) {
            this.personUpdateForm.entityLoaded(entity.mainPerson.person);
            this.personFormGroup.controls.id.setValue(entity.mainPerson.id);
            this.showPersonForm = true;
        } else {
            this.defaultGroup.patchValue(entity.summary);
            this.defaultGroup.patchValue({
                vatRegimeValue: entity.summary.vatRegime?.value,
                mobilePhone: entity.summary.mobilePhone,
                officePhone: entity.summary.officePhone,
                phone: entity.summary.phone
            });
        }

        if (this.isPractitioner) {
            this.practitionerTypeValueFormControl
                .setValue(entity.summary.getAdditionalPropertyByKey(PARTNER_ADDITIONAL_PROPERTY_PRACTITIONER_TYPE_VALUE)?.value?.value);
        }

        this.addDefaultAddress();
    }

    patchParent(typeValue: string, partnerId: number, partnerSummary?: PartnerSummaryModel) {
        console.log('patchParents', this.formGroup.get('parents'));
        if (this.formGroup.get('parents')) {
            (this.formGroup.get('parents') as FormArray).controls.forEach(control => {
                const value = control.value;
                if (value.typeValue === typeValue) {
                    control.patchValue({
                        partnerId
                    });
                    if (partnerSummary) {
                        control.patchValue({
                            partner: partnerSummary
                        });
                    } else {
                        this.partnerSummaryFindOneByIdService.loadEntity(partnerId)
                            .then((value) => {
                                control.patchValue({
                                    partner: value
                                })
                            })
                    }
                }
            });
        }
    }

    addDefaultAddress() {
        if (this.isPractitioner) {
            if (this.addressesFormArray.controls.length === 0) {
                this.addressesFormArray.push(this.newAddress(undefined, PartnerAddressType.MAIN));
            }
        }
        if (this.isPatient) {
            if (this.addressesFormArray.controls.length === 0) {
                this.addressesFormArray.push(this.newAddress(undefined, PartnerAddressType.INVOICE));
            }
        }
    }

    mapMarketingChannnel(marketingChannel: MarketingChannelSummaryModel): UntypedFormGroup {
        let channelName = marketingChannel.type.value;
        if (marketingChannel.type.value === MarketingChannelTypes.CHANNEL_PARTNER_TYPE_VALUE) {
            if (marketingChannel.channelPartnerType.value === PartnerType.PARTNER_TYPE_CUSTOMER) {
                channelName = this.CHANNEL_PATIENT_TYPE_VALUE;
            } else if (marketingChannel.channelPartnerType.value === PRACTITIONER_PARTNER_TYPE) {
                channelName = this.CHANNEL_PRACTITIONER_TYPE_VALUE;
            }
        }

        const patientChannel: UntypedFormGroup = this.formBuilder.group({
            channelId: marketingChannel.type.id,
            channelName
        });

        if (marketingChannel.channelPartner) {
            patientChannel.addControl('channelPartnerId', new UntypedFormControl(marketingChannel.channelPartner?.id, Validators.required));
            patientChannel.addControl('channelPartnerTypeValue',
                new UntypedFormControl(marketingChannel.channelPartnerType?.value, Validators.required));
        }

        return patientChannel;
    }

    buildPayload = (): PartnerSavePayload => {
        const payload: PartnerSavePayload = Object.assign(new PartnerSavePayload(), this.formGroup.value);
        if (this.isPatient || this.isPractitioner) {
            // // console.logByType['form', this.formGroup.value]);
            const partnerPerson: PartnerSavePartnerPersonPayload = new PartnerSavePartnerPersonPayload();
            partnerPerson.typeValue = this.isPatient ? PartnerPersonType.PATIENT : PartnerPersonType.MAIN;
            partnerPerson.person = this.personFormGroup.controls.person.value;
            partnerPerson.id = this.personFormGroup.controls.id.value;
            payload.partnerPerson = partnerPerson;
            payload.name = partnerPerson.person.lastName + ' ' + partnerPerson.person.firstName;

        } else {
            payload.businessNumber = this.defaultGroup.controls?.businessNumber?.value;
            payload.vatRegimeValue = this.defaultGroup.controls?.vatRegimeValue?.value;
            payload.email = this.defaultGroup.controls.email.value;
            payload.name = this.defaultGroup.controls.name.value;
            payload.phone = this.defaultGroup.controls.phone.value;
            payload.mobilePhone = this.defaultGroup.controls.mobilePhone.value;
            payload.officePhone = this.defaultGroup.controls.officePhone.value;
            payload.website = this.defaultGroup.controls.website.value;
        }

        // Parents
        payload.parents = [];
        if (this.formGroup.get('parents')) {
            (this.formGroup.get('parents') as FormArray).controls.forEach(value => {
                if (value.value.partnerId) {
                    payload.parents.push({
                        partnerId: value.value.partnerId,
                        typeValue: value.value.typeValue
                    })
                }
            });
        }

        payload.partnerTypeValue = this.formGroup.controls.partnerTypeValue.value;
        payload.additionalPartnerTypes = this.dialog.input.additionalPartnerTypes;
        payload.addresses = [];
        this.addressesFormArray.controls.forEach(formGroup => {
            const value = formGroup.value;
            console.log('Add addresse', {
                address: value.address,
                id: value.id,
                typeValue: value.typeValue,
                label: value.label
            });
            payload.addresses.push({
                address: value.address,
                id: value.id,
                typeValue: value.typeValue,
                label: value.label
            });
        })

        /* if (this.partnerAddressFormGroup) {
            const partnerAddress: PartnerSavePartnerAddressPayload = this.addressFormGroup.value;
            // // console.logByType['address form group', this.addressFormGroup.value]);
            payload.address = partnerAddress;
        }*/

        if (this.practitionerTypeValueFormControl) {
            // console.logByType'practitionerTypeValueFormControl');
            payload.additionalProperties.push(
                new PartnerSaveAdditionalPropertyPayload('PRACTITIONER_TYPE_VALUE', this.practitionerTypeValueFormControl.value));
        }
        // console.logByType['payload', payload]);
        const marketingChannelValue = this.marketingChannelFormControl?.value;
        payload.marketingChannels = marketingChannelValue ? marketingChannelValue : [];
        payload.note = this.formGroup.controls.note.value;
        return payload;
    }

    resolveReturnUrl = (returnUrl: string, entity: PartnerModel): string => {
        return returnUrl === 'partnerHome' ? 'app/partners/' + entity.summary.id : returnUrl;
    }

    newPersonItemClick() {
        this.showPersonForm = true;
    }

    personItemSelect(person: PersonModel) {
        // We only can change the person in creation mode. So no partner person must be set.
        if (!this.personFormGroup.controls.id.value && !this.dialog.input.id) {
            // // console.logByType['personItemSelect', person.summary]);
            this.personUpdateForm.entityLoaded(person.summary);
            this.showPersonForm = true;
        }
    }

    practitionerTypesChanged(lovValues: LovValueSummary[]) {
        this.practitionerTypes = lovValues;
        this.setDefaultPractitionerType();
    }

    setDefaultPractitionerType() {
        const defaultLovValue = 'MEDECIN_GENERALISTE';
        if (this.practitionerTypeValueFormControl && !this.practitionerTypeValueFormControl.value
            && this.practitionerTypes?.findIndex(value => value.value === defaultLovValue)) {
            this.practitionerTypeValueFormControl.setValue(defaultLovValue);
        }
    }
    newAddress = (partnerAddress: PartnerAddressModel, defaultPartnerTypeAddress: PartnerAddressType = PartnerAddressType.MAIN): UntypedFormGroup => {
        return this.formBuilder.group({
            id: new UntypedFormControl(partnerAddress?.id),
            label: new UntypedFormControl(partnerAddress?.label),
            typeValue: new UntypedFormControl(partnerAddress?.type ? partnerAddress.type.value : defaultPartnerTypeAddress, Validators.required),
            initialAddress: this.formBuilder.control(partnerAddress?.address ? partnerAddress?.address : {
                recipientName: this.defaultGroup.get('name')?.value,
            })
        });
    }

    copyAddress(fromIndex: number, toIndex: number) {
        this.addressesFormArray.controls[toIndex].patchValue({
            address: this.addressesFormArray.controls[fromIndex].value.address
        });

    }

    get isPatient(): boolean {
        return this.formGroup.controls.partnerTypeValue.value === PartnerType.PARTNER_TYPE_PATIENT;
    }

    get isPractitioner(): boolean {
        return this.formGroup.controls.partnerTypeValue.value === PartnerType.PARTNER_TYPE_PRACTITIONER;
    }

    get marketingChannelFormControl(): UntypedFormArray {
        return this.formGroup.get('marketingChannels') as UntypedFormArray;
    }

    get partnerAddressFormGroup(): UntypedFormGroup {
        return this.formGroup.get('address') as UntypedFormGroup;
    }

    get partnerDefaultGroupFormGroup(): UntypedFormGroup {
        return this.formGroup.get('defaultGroup') as UntypedFormGroup;
    }

    get partnerPersonFormGroup(): UntypedFormGroup {
        return this.formGroup.get('personGroup') as UntypedFormGroup;
    }

    get practitionerTypeValueFormControl(): UntypedFormControl {
        return this.formGroup.get('practitionerTypeValue') as UntypedFormControl;
    }


    get email() {
        return this.defaultGroup.get('email');
    }

    get phone(): AbstractControl {
        return this.defaultGroup.get('phone');
    }

    get mobilePhone(): AbstractControl {
        return this.defaultGroup.get('mobilePhone');
    }

    get officePhone(): AbstractControl {
        return this.defaultGroup.get('officePhone');
    }

    get officeControl(): AbstractControl {
        return this.formGroup.get('officeId');
    }

    get formGroup2(): FormGroup {
        console.log(this.formGroup);
        return this.formGroup;
    }

}
